/**
 * Typed API client factories using openapi-fetch.
 *
 * These factories create HTTP clients whose request and response types are
 * enforced at compile time by the generated OpenAPI path types. This prevents
 * the class of bugs where fetch().json() returns `any` and field name
 * mismatches go undetected until runtime.
 *
 * Usage:
 *   import { createPeFundClient } from '@aaimplatform/institutional-types/api-clients';
 *   const client = createPeFundClient('http://localhost:9390');
 *   const { data, error } = await client.GET('/api/v1/pe/funds');
 *   // data is typed -- tsc catches .funds vs .data mismatches
 *
 * Generic usage for any spec:
 *   import { createTypedClient } from '@aaimplatform/institutional-types/api-clients';
 *   import type { paths } from '@aaimplatform/institutional-types/authentication-service';
 *   const client = createTypedClient<paths>('http://localhost:9350/api/v1');
 *   (authentication-service path keys are still relative, so its base carries
 *   /api/v1; a spec whose keys carry the prefix takes the bare origin, as the PE
 *   clients below do. GH #2844, api-version-canonical.md.)
 *
 * DEFAULT_BASE constants below read import.meta.env at module-init time so
 * Vite bakes the per-SPA build-arg into the JS bundle. Empty-string fallback
 * is intentional per F-VITE-AUTH-LOCALHOST-DEFAULT-FEDERATED-SPAS: a missing
 * build-arg produces a same-origin relative-URL fetch that 404s visibly on
 * the SPA's own origin rather than silently issuing CORS requests to a
 * user's localhost. The `typeof import.meta !== 'undefined'` guard preserves
 * Node test reachability where the package is imported but import.meta.env
 * is undefined; tests should pass an explicit baseUrl override.
 *
 * See knowledge-graph/projects/aaim/technology-innovation/incidents/2026-05-13-ops-console-auth-url-localhost-bake.md
 */

/// <reference path="./vite-env.d.ts" />
import createOpenapiClient, { type ClientOptions } from 'openapi-fetch';

/**
 * Session-cookie default (browser-token-storage-hardening /
 * .claude/rules/no-browser-token-storage.md): every client created by this
 * module defaults to `credentials: 'include'` so the browser attaches the
 * HttpOnly session cookie on each request. The federated SPAs hold NO bearer
 * token in browser storage -- the cookie IS the session. A caller-supplied
 * `credentials` still wins via the spread. Harmless no-op for the server-side
 * internal-sync clients (Node fetch has no cookie jar; those authenticate with
 * explicit X-Internal-Token / Bearer headers).
 */
function createClient<Paths extends {}>(clientOptions: ClientOptions = {}) {
  return createOpenapiClient<Paths>({ credentials: 'include', ...clientOptions });
}

import type { paths as PeFundAnalyticsPaths } from '../generated/pe-fund-analytics.js';
import type { paths as PeValuationPaths } from '../generated/pe-valuation.js';
import type { paths as PePortfolioCompanyPaths } from '../generated/pe-portfolio-company.js';
import type { paths as AuthenticationPaths } from '../generated/authentication-service.js';
import type { paths as PlatformAdminPaths } from '../generated/platform-admin.js';
import type { paths as InstitutionalPlatformPaths } from '../generated/institutional-platform.js';
import type { paths as LoanManagementPaths } from '../generated/loan-management.js';
import type { paths as LoanOriginationPaths } from '../generated/loan-origination.js';
import type { paths as CompliancePaths } from '../generated/compliance-service.js';
import type { paths as CoinbaseAdapterPaths } from '../generated/coinbase-adapter.js';
import type { paths as AnalyticsPaths } from '../generated/analytics-service.js';
import type { paths as AuditTrailPaths } from '../generated/audit-trail.js';
import type { paths as RiskAssessmentPaths } from '../generated/risk-assessment.js';
import type { paths as EscalationPaths } from '../generated/escalation-service.js';
import type { paths as WorkflowPaths } from '../generated/workflow-engine.js';
import type { paths as FinancialDataPaths } from '../generated/financial-data.js';
import type { paths as CollateralizationPaths } from '../generated/collateralization-engine.js';
import type { paths as CollateralWellnessPaths } from '../generated/collateral-wellness.js';
import type { paths as UnderwritingPaths } from '../generated/underwriting-engine.js';
import type { paths as PortfolioValuationPaths } from '../generated/portfolio-valuation.js';
import type { paths as EconomicModelingPaths } from '../generated/economic-modeling.js';
import type { paths as AdvisorToolsPaths } from '../generated/advisor-tools.js';
import type { paths as PropertyTitleAdminPaths } from '../generated/property-title-admin.js';
import type { paths as TrustCenterPaths } from '../generated/trust-center.js';
import type { paths as TrustCenterMonitorPaths } from '../generated/trust-center-monitor.js';
import type { paths as IdentityProviderRegistryPaths } from '../generated/identity-provider-registry.js';
import type { paths as RbacManagementPaths } from '../generated/rbac-management.js';
import type { paths as RegulatoryReadinessPaths } from '../generated/regulatory-readiness.js';
import type { paths as UccFilingLifecyclePaths } from '../generated/ucc-filing-lifecycle.js';
import type { paths as UccEntitiesPaths } from '../generated/ucc-entities.js';
import type { paths as UccFilingSubmissionPaths } from '../generated/ucc-filing-submission.js';
import type { paths as UccPaperFilingPaths } from '../generated/ucc-paper-filing.js';
import type { paths as UccSearchPaths } from '../generated/ucc-search.js';
import type { paths as UccFeeReconciliationPaths } from '../generated/ucc-fee-reconciliation.js';
import type { paths as UccFilingAnalyticsPaths } from '../generated/ucc-filing-analytics.js';
import type { paths as UccFilingAuditCompliancePaths } from '../generated/ucc-filing-audit-compliance.js';
import type { paths as UccMultiStateFilingPaths } from '../generated/ucc-multi-state-filing.js';
import type { paths as UccBulkOperationsPaths } from '../generated/ucc-bulk-operations.js';
import type { paths as UccFilingWebhooksPaths } from '../generated/ucc-filing-webhooks.js';
import type { paths as UccJurisdictionRulesPaths } from '../generated/ucc-jurisdiction-rules.js';
import type { paths as LoanSyndicationProtocolPaths } from '../generated/loan-syndication-protocol.js';
import type { paths as CooperativeReserveTokenPaths } from '../generated/cooperative-reserve-token.js';
import type { paths as MemberLiquidityOverdraftPaths } from '../generated/tempo.js';
import type { paths as SharenexusPlatformPaths } from '../generated/sharenexus-platform.js';
import type { paths as FiatOracleServicePaths } from '../generated/fiat-oracle-service.js';
import type { paths as X402PaymentProtocolPaths } from '../generated/x402-payment-protocol.js';
import type { paths as FiPolicySyncPaths } from '../generated/fi-policy-sync.js';
import type { paths as FiTenantProvisionPaths } from '../generated/fi-tenant-provision.js';
import type { paths as CollateralPledgePaths } from '../generated/collateral-pledge.js';
import type { paths as FinancialProviderPaths } from '../generated/financial-provider.js';
import type { paths as CoreTenantProvisioningPaths } from '../generated/core-tenant-provisioning.js';

// ---------------------------------------------------------------------------
// Generic factory -- use with any generated paths type
// ---------------------------------------------------------------------------

/**
 * Create a type-safe HTTP client for any OpenAPI spec.
 *
 * @param baseUrl - Service origin, no path (e.g., 'http://localhost:9390'). Canonical spec
 *   path keys are absolute (`/api/v1/<resource>`, .claude/rules/api-version-canonical.md).
 *   A spec still keyed relative is a KEY row of
 *   .claude/manifests/api-path/route-shape-inventory.txt, owned by the unit that re-keys it.
 */
export function createTypedClient<Paths extends {}>(baseUrl: string) {
  return createClient<Paths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// PE Valuation Service (port 9390)
// ---------------------------------------------------------------------------

const PE_VALUATION_ORIGIN =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PE_VALUATION_URL) || '';

// Every pe-valuation path key in the generated specs carries the full
// `/api/v1/pe/...` prefix the service mounts (services/pe-valuation/src/server.ts
// :124, :127, :130), so the base is the bare origin. The GH #2844 operator ruling
// of 2026-09-15 moved the prefix out of the specs' `servers:` block and into the
// path keys; the URL openapi-fetch composes is byte-identical across that move,
// because createFinalURL concatenates base and path (openapi-fetch 0.17.0
// dist/index.mjs:489) and an unset origin leaves an empty base:
//   empty env -> `/api/v1/pe/funds`
//   set env   -> `<origin>/api/v1/pe/funds`
// PeClientBase.test.tsx pins both branches. On prod each SPA Dockerfile bakes
// VITE_PE_VALUATION_URL as the SPA's OWN host origin (advisor-tools
// https://advisor.aaim.com, strategy-tools https://strategy.aaim.com; the
// manifest build_args carry the same values so bundle parity can assert them),
// and admin-portal leaves it empty, so every SPA issues same-origin requests
// that the load balancer routes to pe-valuation by a `<host>/api/v1/pe/*` path
// rule (GH #1671). The value is non-empty on the two SPAs
// only because their nav gates (Layout.tsx, StrategyToolsLayout.tsx) hide the
// Private Equity group on an empty origin; the SPA nginx `location /api/v1/pe`
// blocks never see LB traffic.
const PE_DEFAULT_BASE = PE_VALUATION_ORIGIN;

export function createPeFundClient(baseUrl = PE_DEFAULT_BASE) {
  return createClient<PeFundAnalyticsPaths>({ baseUrl });
}

export function createPeValuationClient(baseUrl = PE_DEFAULT_BASE) {
  return createClient<PeValuationPaths>({ baseUrl });
}

export function createPePortfolioClient(baseUrl = PE_DEFAULT_BASE) {
  return createClient<PePortfolioCompanyPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// aaim-core Tenant Provisioning (port 9551, in-cluster aaim-core authorization)
//
// Server-side only: consumed by the platform-admin-api provisioning saga
// (ADR-011 / rbac-03c). The institutional orchestrator calls aaim-core to mint
// the core Tenant (POST) and to soft-deactivate it on compensation (DELETE).
// baseUrl is the AAIM_CORE_AUTH_URL env value; the default mirrors the
// in-cluster service hostname used by the existing inst<->core proxies.
// ---------------------------------------------------------------------------

const CORE_AUTH_DEFAULT_BASE = 'http://aaim-core-authorization:9551';

export function createCoreTenantProvisioningClient(baseUrl = CORE_AUTH_DEFAULT_BASE) {
  return createClient<CoreTenantProvisioningPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// API Gateway (port 9350)
// ---------------------------------------------------------------------------

const GATEWAY_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_GATEWAY_URL) || '';

export function createAuthenticationClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<AuthenticationPaths>({ baseUrl });
}

export function createInstitutionalPlatformClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<InstitutionalPlatformPaths>({ baseUrl });
}

export function createLoanManagementClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<LoanManagementPaths>({ baseUrl });
}

export function createLoanOriginationClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<LoanOriginationPaths>({ baseUrl });
}

export function createComplianceClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<CompliancePaths>({ baseUrl });
}

export function createAnalyticsClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<AnalyticsPaths>({ baseUrl });
}

export function createAuditTrailClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<AuditTrailPaths>({ baseUrl });
}

export function createRiskAssessmentClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<RiskAssessmentPaths>({ baseUrl });
}

export function createEscalationClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<EscalationPaths>({ baseUrl });
}

export function createWorkflowClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<WorkflowPaths>({ baseUrl });
}

export function createFinancialDataClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<FinancialDataPaths>({ baseUrl });
}

export function createCollateralizationClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<CollateralizationPaths>({ baseUrl });
}

export function createCollateralWellnessClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<CollateralWellnessPaths>({ baseUrl });
}

export function createUnderwritingClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<UnderwritingPaths>({ baseUrl });
}

export function createPortfolioValuationClient(baseUrl = GATEWAY_DEFAULT_BASE) {
  return createClient<PortfolioValuationPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// Platform Admin API (port 9302)
// ---------------------------------------------------------------------------

const PLATFORM_ADMIN_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_ADMIN_API_URL) || '';

export function createPlatformAdminClient(baseUrl = PLATFORM_ADMIN_DEFAULT_BASE) {
  return createClient<PlatformAdminPaths>({ baseUrl });
}

export function createPropertyTitleAdminClient(baseUrl = PLATFORM_ADMIN_DEFAULT_BASE) {
  return createClient<PropertyTitleAdminPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// Coinbase Adapter (port 9310)
// ---------------------------------------------------------------------------

const COINBASE_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_COINBASE_ADAPTER_URL) || '';

export function createCoinbaseAdapterClient(baseUrl = COINBASE_DEFAULT_BASE) {
  return createClient<CoinbaseAdapterPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// Strategy Tools API (port 9385)
// ---------------------------------------------------------------------------

const STRATEGY_TOOLS_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_STRATEGY_TOOLS_API_URL) || '';

// credentials:'include' so the HttpOnly session cookie rides on every request
// (strategy-tools cookie-session cutover, SEC-V04 / no-browser-token-storage).
// This factory is consumed only by strategy-tools, so the cookie default is
// scoped to that SPA; the inline createEconomicModelingClient('') call sites in
// strategy-tools inherit it without a per-call-site change.
export function createEconomicModelingClient(baseUrl = STRATEGY_TOOLS_DEFAULT_BASE) {
  return createClient<EconomicModelingPaths>({ baseUrl, credentials: 'include' });
}

// ---------------------------------------------------------------------------
// Advisor Tools API (port 9350 via Gateway)
//
// Sprint C6 A-1: Advisor-tools paths intentionally retain the /api/ prefix in
// their OpenAPI spec to match the api-gateway mount pattern (unversioned
// per-resource mounts: advisors, clients, proposals, ...; unit B11b re-keys the
// spec and B22 retires the gateway). The server URL therefore
// drops /api/v1 so concatenation produces /api/<resource> rather than
// /api/v1/api/<resource> (the path-doubling bug fixed 2026-04-27).
// ---------------------------------------------------------------------------

const ADVISOR_TOOLS_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ADVISOR_TOOLS_API_URL) || '';

// credentials:'include' so the advisor-tools HttpOnly session cookie rides on every
// request (cookie-session cutover, SEC-V04 / GH #1780). Advisor-tools is the only consumer.
export function createAdvisorToolsClient(baseUrl = ADVISOR_TOOLS_DEFAULT_BASE) {
  return createClient<AdvisorToolsPaths>({ baseUrl, credentials: 'include' });
}

// ---------------------------------------------------------------------------
// Trust Center API (port 9391)
// ---------------------------------------------------------------------------

const TRUST_CENTER_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TRUST_CENTER_API_URL) || '';

export function createTrustCenterClient(baseUrl = TRUST_CENTER_DEFAULT_BASE) {
  return createClient<TrustCenterPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// Trust Center Monitor (port 9392)
// ---------------------------------------------------------------------------

const TRUST_CENTER_MONITOR_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TRUST_CENTER_MONITOR_URL) || '';

export function createTrustCenterMonitorClient(baseUrl = TRUST_CENTER_MONITOR_DEFAULT_BASE) {
  return createClient<TrustCenterMonitorPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// Authorization Service (port 9551)
// ---------------------------------------------------------------------------

const AUTHORIZATION_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUTH_SERVICE_URL) || '';

/**
 * Create a client for the identity provider registry API.
 * Manages OIDC, SAML, FI-delegated, OAuth2, and API key providers.
 */
export function createIdentityProviderClient(baseUrl = AUTHORIZATION_DEFAULT_BASE) {
  return createClient<IdentityProviderRegistryPaths>({ baseUrl });
}

/**
 * Create a client for the RBAC management API.
 * Manages roles, permissions, user role assignments, and effective permissions.
 */
export function createRbacClient(baseUrl = AUTHORIZATION_DEFAULT_BASE) {
  return createClient<RbacManagementPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// Regulatory Readiness Engine (port 9395)
// ---------------------------------------------------------------------------

const REGULATORY_READINESS_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_REGULATORY_READINESS_URL) || '';

export function createRegulatoryReadinessClient(baseUrl = REGULATORY_READINESS_DEFAULT_BASE) {
  return createClient<RegulatoryReadinessPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// UCC Filing Service (port 9554)
// ---------------------------------------------------------------------------

const UCC_FILING_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_UCC_FILING_URL) || '';

export function createUccFilingLifecycleClient(baseUrl = UCC_FILING_DEFAULT_BASE) {
  return createClient<UccFilingLifecyclePaths>({ baseUrl });
}

export function createUccEntitiesClient(baseUrl = UCC_FILING_DEFAULT_BASE) {
  return createClient<UccEntitiesPaths>({ baseUrl });
}

export function createUccFilingAnalyticsClient(baseUrl = UCC_FILING_DEFAULT_BASE) {
  return createClient<UccFilingAnalyticsPaths>({ baseUrl });
}

export function createUccBulkOperationsClient(baseUrl = UCC_FILING_DEFAULT_BASE) {
  return createClient<UccBulkOperationsPaths>({ baseUrl });
}

export function createUccFilingAuditComplianceClient(baseUrl = UCC_FILING_DEFAULT_BASE) {
  return createClient<UccFilingAuditCompliancePaths>({ baseUrl });
}

export function createUccMultiStateFilingClient(baseUrl = UCC_FILING_DEFAULT_BASE) {
  return createClient<UccMultiStateFilingPaths>({ baseUrl });
}

export function createUccFilingWebhooksClient(baseUrl = UCC_FILING_DEFAULT_BASE) {
  return createClient<UccFilingWebhooksPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// UCC Jurisdiction Rules Service (port 9553)
// ---------------------------------------------------------------------------

const UCC_JURISDICTION_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_UCC_JURISDICTION_URL) || '';

export function createUccJurisdictionRulesClient(baseUrl = UCC_JURISDICTION_DEFAULT_BASE) {
  return createClient<UccJurisdictionRulesPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// UCC Filing Submission Service (port 9555)
// ---------------------------------------------------------------------------

const UCC_SUBMISSION_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_UCC_SUBMISSION_URL) || '';

export function createUccFilingSubmissionClient(baseUrl = UCC_SUBMISSION_DEFAULT_BASE) {
  return createClient<UccFilingSubmissionPaths>({ baseUrl });
}

export function createUccPaperFilingClient(baseUrl = UCC_SUBMISSION_DEFAULT_BASE) {
  return createClient<UccPaperFilingPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// UCC Search Service (port 9556)
// ---------------------------------------------------------------------------

const UCC_SEARCH_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_UCC_SEARCH_URL) || '';

export function createUccSearchClient(baseUrl = UCC_SEARCH_DEFAULT_BASE) {
  return createClient<UccSearchPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// UCC Fee Reconciliation Service (port 9557)
// ---------------------------------------------------------------------------

const UCC_FEE_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_UCC_FEE_URL) || '';

export function createUccFeeReconciliationClient(baseUrl = UCC_FEE_DEFAULT_BASE) {
  return createClient<UccFeeReconciliationPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// Loan Syndication Protocol (port 9570)
// ---------------------------------------------------------------------------

const LOAN_SYNDICATION_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_LSP_URL) || '';

export function createLoanSyndicationClient(baseUrl = LOAN_SYNDICATION_DEFAULT_BASE) {
  return createClient<LoanSyndicationProtocolPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// Cooperative Reserve Token (port 9571)
// ---------------------------------------------------------------------------

const CRT_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CRT_URL) || '';

export function createCooperativeReserveTokenClient(baseUrl = CRT_DEFAULT_BASE) {
  return createClient<CooperativeReserveTokenPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// Member Liquidity Engine (port 9575)
// ---------------------------------------------------------------------------

const MEMBER_LIQUIDITY_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_MLE_URL) || '';

/**
 * Member Liquidity Engine client (overdraft-coverage arc OC-3). Spec paths
 * declare the full /api/v1/tempo/* prefix, so the default baseUrl is
 * same-origin '' -- the SPA dev proxy / nginx / LB route the prefix to the
 * service (the loan-syndication pattern).
 */
export function createMemberLiquidityClient(baseUrl = MEMBER_LIQUIDITY_DEFAULT_BASE) {
  return createClient<MemberLiquidityOverdraftPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// ShareNexus Platform (port 9572)
// ---------------------------------------------------------------------------

const SHARENEXUS_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SHARENEXUS_URL) || '';

export function createSharenexusPlatformClient(baseUrl = SHARENEXUS_DEFAULT_BASE) {
  return createClient<SharenexusPlatformPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// Fiat Oracle Service (port 9573)
// ---------------------------------------------------------------------------

const FIAT_ORACLE_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIAT_ORACLE_URL) || '';

export function createFiatOracleClient(baseUrl = FIAT_ORACLE_DEFAULT_BASE) {
  return createClient<FiatOracleServicePaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// x402 Payment Protocol (port 9574)
// ---------------------------------------------------------------------------

const X402_PAYMENT_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_X402_PAYMENT_URL) || '';

export function createX402PaymentClient(baseUrl = X402_PAYMENT_DEFAULT_BASE) {
  return createClient<X402PaymentProtocolPaths>({ baseUrl });
}

// ---------------------------------------------------------------------------
// Consumer API internal sync (port 9401)
// ---------------------------------------------------------------------------

const CONSUMER_API_DEFAULT_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CONSUMER_API_URL) || '';

export function createFiPolicySyncClient(baseUrl = CONSUMER_API_DEFAULT_BASE) {
  return createClient<FiPolicySyncPaths>({ baseUrl });
}

// ftc-03: client for the internal id-pinned consumer FiTenant provisioning
// endpoint (saga third leg). Same CONSUMER_API base + X-Internal-Token auth as
// createFiPolicySyncClient.
export function createFiTenantProvisionClient(baseUrl = CONSUMER_API_DEFAULT_BASE) {
  return createClient<FiTenantProvisionPaths>({ baseUrl });
}

/**
 * Client for consumer collateral-pledge endpoints (Sprint 172 D-006).
 * Used by platform-admin-api to proxy FI-admin certificate and
 * audit-bundle downloads to the consumer service.
 */
export function createCollateralPledgeClient(baseUrl = CONSUMER_API_DEFAULT_BASE) {
  return createClient<CollateralPledgePaths>({ baseUrl });
}

// Connector-attest client (M-PILOT-03 / ADR-009). The financial-provider spec declares
// POST /connector/attest; consumer-api mounts that router under its unversioned
// financial-provider prefix (the string CONNECTOR_ATTEST_DEFAULT_BASE appends below), so
// the live URL is that prefix plus /connector/attest. The default baseUrl appends the
// prefix to the consumer origin so the typed `/connector/attest` path template resolves
// to the live mount. Same-origin (VITE_CONSUMER_API_URL empty) yields the bare prefix,
// which the SPA nginx block for that prefix proxies to consumer-api. Unit B14 moves the
// mount under /api/v1 and makes this base origin-only.
const CONNECTOR_ATTEST_DEFAULT_BASE =
  ((typeof import.meta !== 'undefined' && import.meta.env?.VITE_CONSUMER_API_URL) || '') + '/api/financial';

// credentials:'include' so the advisor's HttpOnly session cookie carries auth on the
// connector-attest call (cookie-session cutover, SEC-V04 / GH #1780). The attest POST may
// additionally pass a fresh step-up token as an explicit Authorization header, which takes
// precedence over the cookie.
export function createConnectorAttestClient(baseUrl = CONNECTOR_ATTEST_DEFAULT_BASE) {
  return createClient<FinancialProviderPaths>({ baseUrl, credentials: 'include' });
}
